import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, GraduationCap, Clock, Star, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { useReducedMotion } from '@/hooks/use-reduced-motion';

const Hero = () => {
  const { t, isRTL } = useLanguage();
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      className="relative overflow-hidden bg-hero-gradient dark:bg-hero-gradient-dark min-h-[600px] flex items-center"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Geometric pattern */}
      <div className="absolute inset-0 islamic-pattern-overlay opacity-60" />

      {/* Gradient orbs */}
      <div className="absolute top-10 end-[10%] w-[500px] h-[500px] rounded-full bg-amber-400/8 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 start-[5%] w-[400px] h-[400px] rounded-full bg-emerald-300/8 blur-[80px] pointer-events-none" />

      {/* Floating calligraphy */}
      <motion.div
        lang="ar"
        className="absolute top-20 end-[15%] text-white/[0.04] font-arabic text-[120px] leading-none select-none pointer-events-none hidden lg:block"
        animate={prefersReducedMotion ? undefined : { y: [0, -12, 0] }}
        transition={prefersReducedMotion ? { duration: 0 } : { duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      >
        هُدًى
      </motion.div>

      <div className="container relative z-10 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-1.5 mb-6"
            >
              <Star className="h-3.5 w-3.5 text-amber-400" aria-hidden="true" />
              <span className="text-emerald-100 text-sm font-medium">
                {isRTL ? <span lang="ar">تعلم القرآن مع معلمين معتمدين</span> : 'Learn Quran with certified teachers'}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-[1.15]">
              {t('hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-emerald-100/80 mb-8 max-w-lg leading-relaxed">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Button
                asChild
                size="lg"
                className="bg-amber-500 hover:bg-amber-500/90 text-amber-950 font-semibold rounded-xl px-7 h-12 text-base shadow-glow-gold"
              >
                <Link to="/register" className="flex items-center gap-2">
                  {t('hero.findTeacher')}
                  <ArrowRight className="h-4 w-4 rtl-mirror" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-xl px-7 h-12 text-base"
              >
                <Link to="/register/teacher">{t('hero.becomeTeacher')}</Link>
              </Button>
            </div>

            {/* Stats */}
            <motion.dl
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex gap-8"
            >
              {[
                { value: '500+', label: isRTL ? 'معلم معتمد' : 'Certified Teachers' },
                { value: '10K+', label: isRTL ? 'طالب نشط' : 'Active Students' },
                { value: '50K+', label: isRTL ? 'جلسة مكتملة' : 'Sessions Completed' },
              ].map((stat, i) => (
                <div key={i} className="text-center sm:text-start">
                  <dt className="text-xs text-emerald-200/70 mt-0.5 order-2">{stat.label}</dt>
                  <dd className="text-2xl font-bold text-amber-400">{stat.value}</dd>
                </div>
              ))}
            </motion.dl>
          </motion.div>

          {/* Visual Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Main card */}
              <div className="bg-white/10 backdrop-blur-lg border border-white/15 rounded-2xl p-8 shadow-2xl">
                {/* Quranic verse */}
                <div className="text-center mb-8">
                  <p lang="ar" className="font-quran text-4xl text-amber-400 leading-loose mb-3" dir="rtl">
                    ذَٰلِكَ الْكِتَابُ لَا رَيْبَ ۛ فِيهِ ۛ
                  </p>
                  <p lang="ar" className="font-quran text-4xl text-amber-400 leading-loose mb-4" dir="rtl">
                    هُدًى لِّلْمُتَّقِينَ
                  </p>
                  <p className="text-emerald-200/60 text-sm italic">
                    "This is the Book about which there is no doubt,
                    <br />a guidance for those conscious of Allah."
                  </p>
                  <p className="text-emerald-300/40 text-xs mt-2">— Al-Baqarah 2:2</p>
                </div>

                {/* Feature pills */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { icon: BookOpen, label: isRTL ? 'دروس مخصصة' : 'Personalized' },
                    { icon: GraduationCap, label: isRTL ? 'معلمون مؤهلون' : 'Qualified' },
                    { icon: Clock, label: isRTL ? 'جدول مرن' : 'Flexible' },
                  ].map(({ icon: Icon, label }, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ scale: 1.05, y: -2 }}
                      className="flex flex-col items-center text-center p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl cursor-default"
                    >
                      <Icon className="h-6 w-6 text-amber-400 mb-2" />
                      <span className="text-white/80 text-xs font-medium">{label}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Floating accent card */}
              <motion.div
                animate={prefersReducedMotion ? undefined : { y: [0, -8, 0] }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-4 -start-6 bg-white dark:bg-card rounded-xl shadow-soft-lg p-3 border border-border/50"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                    <Star className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground">4.9/5</p>
                    <p className="text-[10px] text-muted-foreground">{isRTL ? 'تقييم المعلمين' : 'Teacher Rating'}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
